import React, { useEffect, useState } from "react";
import Navbar from "./Navbar";
import axiosInstance from "../config/axios";

const formatSentAt = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch (_) {
    return String(value);
  }
};

export default function TrustMailTrackerPage() {
  const [entries, setEntries] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");

  const load = async (overrides = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = {
        limit: 200,
        invoiceType: "trust",
        q: overrides.q !== undefined ? overrides.q : q,
      };
      if (!params.q) delete params.q;
      const res = await axiosInstance.get("/invoice-sends", { params });
      setEntries(Array.isArray(res.data?.logs) ? res.data.logs : []);
      setTotal(Number(res.data?.total) || 0);
    } catch (err) {
      setError(
        err.response?.data?.message || "Failed to load trust mail tracker."
      );
      setEntries([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                Trust Mail Tracker
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                Monitor trust deposit receipts emailed from Trade Info Trust.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => load()}
                className="px-4 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
              >
                Refresh
              </button>
            </div>
          </div>

          <form
            className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 mb-4 flex flex-col sm:flex-row gap-3"
            onSubmit={(e) => {
              e.preventDefault();
              load();
            }}
          >
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search trade #, recipient, property, sender…"
              className="flex-1 rounded-md border-gray-300 text-sm focus:border-blue-700 focus:ring-blue-700"
            />
            <button
              type="submit"
              className="px-4 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900 text-sm"
            >
              Search
            </button>
          </form>

          {error ? (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          ) : null}

          <p className="text-xs text-gray-500 mb-2">
            Showing {entries.length}
            {total ? ` of ${total}` : ""} recorded trust mail
            {total === 1 ? "" : "s"}.
          </p>

          {loading ? (
            <p className="text-gray-600">Loading…</p>
          ) : entries.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-lg shadow p-8 text-center text-gray-600">
              No trust deposit receipts have been emailed yet. Sends from Trust
              Information will appear here automatically.
            </div>
          ) : (
            <div className="bg-white border border-gray-200 rounded-lg shadow overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-100 text-left text-gray-700">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Sent</th>
                    <th className="px-4 py-3 font-semibold">Trade</th>
                    <th className="px-4 py-3 font-semibold">Type</th>
                    <th className="px-4 py-3 font-semibold">Recipient</th>
                    <th className="px-4 py-3 font-semibold">Property</th>
                    <th className="px-4 py-3 font-semibold">Amount</th>
                    <th className="px-4 py-3 font-semibold">Sent by</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((row) => (
                    <tr
                      key={row.id}
                      className="border-t border-gray-100 hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 whitespace-nowrap text-gray-900">
                        {formatSentAt(row.sentAt)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap font-medium text-gray-900">
                        {row.tradeNumber ? `#${row.tradeNumber}` : "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        Trust deposit
                      </td>
                      <td className="px-4 py-3 break-all text-gray-800">
                        {row.recipientEmail || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700 max-w-xs">
                        {row.propertyAddress || "—"}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                        {row.amountDue || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        <div className="break-all">
                          {row.sentByEmail || row.sentByName || "—"}
                        </div>
                        {row.sentByEmail &&
                        row.sentByName &&
                        row.sentByName !== row.sentByEmail ? (
                          <div className="text-[11px] text-gray-500">
                            {row.sentByName}
                          </div>
                        ) : null}
                        {row.sentByRole ? (
                          <div className="text-[11px] text-gray-500 capitalize">
                            {row.sentByRole}
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
